import React, { FC } from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { AddLiquiditySectionTitle } from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquiditySectionTitle';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { Bound, Currency, Price, Token } from '@cryptoalgebra/kim-sdk';
import { IDerivedMintInfo } from '@app/state/mintStore';
import PresetTabs from '../PresetTabs';
import RangeSelector from '../RangeSelector';

interface Props {
  price: Price<Token, Token> | undefined;
  mintInfo: IDerivedMintInfo;
  currencyA: Currency | undefined;
  currencyB: Currency | undefined;
  startPriceTypedValue: string;
  onStartPriceInput: (val: string) => void;
}

export const AddLiquidityRangeSelector: FC<Props> = ({
  price,
  mintInfo,
  currencyA,
  currencyB,
  startPriceTypedValue,
  onStartPriceInput
}) => {
  const { [Bound.LOWER]: priceLower, [Bound.UPPER]: priceUpper } =
    mintInfo.pricesAtTicks;

  const areCurrenciesSelected = !!(currencyB && currencyA);
  const isRangeSelected = priceLower !== undefined && priceUpper !== undefined;

  return (
    <VStack gap={4} width="100%">
      <AddLiquiditySectionTitle
        isCompleted={!!price && isRangeSelected}
        label="Set a range"
      />
      {areCurrenciesSelected && (
        <>
          <HStack p="0" gap="12px" w="100%">
            {mintInfo.noLiquidity && (
              <VStack alignItems="flex-start" width="100%">
                <Text>
                  Set initial {currencyA?.symbol} to {currencyB?.symbol} price
                </Text>
                <Box
                  sx={{
                    // padding: '',
                    border: '1px solid',
                    borderRadius: '4px',
                    borderColor: 'neutral.700',
                    width: '100%'
                  }}
                >
                  <DebouncedInput
                    textAlign="left"
                    amount={startPriceTypedValue}
                    disabled={false}
                    onChange={val => onStartPriceInput(val)}
                  />
                </Box>
              </VStack>
            )}
          </HStack>
          {price && (
            <HStack
              alignItems="flex-start"
              width="100%"
              sx={{
                padding: '12px',
                border: '1px solid',
                borderRadius: '4px',
                borderColor: 'neutral.700',
                background: 'neutral.800'
              }}
            >
              <VStack alignItems="flex-start">
                <Text fontSize="12px" color="neutral.400">
                  Current price
                </Text>
                <Text>
                  1 {price?.baseCurrency.symbol}&nbsp;-&nbsp;
                  {price?.toSignificant(5)} {price?.quoteCurrency.symbol}
                </Text>
              </VStack>
            </HStack>
          )}
          <VStack alignItems="flex-start" width="100%">
            <Text>Presets</Text>
            <PresetTabs
              currencyA={currencyA}
              currencyB={currencyB}
              mintInfo={mintInfo}
            />
          </VStack>
          {price && (
            <RangeSelector
              priceLower={priceLower}
              priceUpper={priceUpper}
              currencyA={currencyA}
              currencyB={currencyB}
              mintInfo={mintInfo}
              disabled={!startPriceTypedValue && !mintInfo.price}
            />
          )}
        </>
      )}
    </VStack>
  );
};
